import { ReserveStatus } from '@/domain/market-info/reserve-status'
import { Token } from '@/domain/types/Token'
import { ColorFilter } from '@/ui/atoms/color-filter/ColorFilter'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { cn } from '@/ui/utils/style'
import { Info } from '@/ui/molecules/info/Info'

import { FrozenPill } from '../../frozen-pill/FrozenPill'
import { PausedPill } from '../../paused-pill/PausedPill'
import { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import { PointsBadge } from '../../points-badge/PointsBadge'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { GradientBadge } from '@/ui/atoms/badge/Badge'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useDisplayToken } from '@/domain/hooks/useDisplayToken'

interface TokenWithLogoProps {
  token: Token
  reserveStatus: ReserveStatus
  isCombinedBalance?: boolean
  hideSymbol?: boolean
  hideName?: boolean
  className?: string
  withNewBadge?: boolean
  launchDate?: string
  points?: {
    multiplier?: number
    action: 'deposit' | 'borrow' | 'lend'
    partnerPoints?: IsolatedPartnerPointsPerPair[]
    extraIncentives?: string
    asset: TokenSymbol
  }
}

export function TokenWithLogo({
  token,
  reserveStatus,
  isCombinedBalance,
  hideName,
  hideSymbol,
  points,
  className,
  withNewBadge,
  launchDate,
}: TokenWithLogoProps) {
  const isPaused = reserveStatus === 'paused'
  const isFrozen = reserveStatus === 'frozen'
  const displayToken = useDisplayToken(token)

  return (
    <div className={cn('flex items-center gap-2 py-2', className)}>
      <div className="flex shrink-0">
        <ColorFilter variant={isPaused ? 'red' : 'none'}>
          <TokenIcon token={displayToken} className="h-6 w-6" />
        </ColorFilter>
      </div>
      <div className="flex flex-col items-start">
        <div className="flex items-center gap-2">
          {!hideName && (
            <div
              className={cn('font-semibold text-base text-foreground', {
                'font-normal text-sm': hideSymbol,
                'text-red-500': isPaused,
              })}
            >
              {displayToken.name}
            </div>
          )}
          {withNewBadge && !hideName && (
            <GradientBadge>
              <Typography variant="prompt" gradient>
                New!
              </Typography>
            </GradientBadge>
          )}
          {launchDate && (
            <GradientBadge>
              <Typography variant="prompt" gradient>
                Launches {launchDate}
              </Typography>
            </GradientBadge>
          )}
        </div>
        <div
          className={cn('flex', {
            'items-center gap-2': !hideName,
            'flex-col items-start': hideName,
          })}
        >
          {!hideSymbol && (
            <div className="flex items-center gap-2">
              <div
                className={cn('font-normal text-muted-foreground text-sm leading-none', {
                  'font-semibold text-base text-foreground': hideName,
                })}
              >
                {displayToken.symbol}
              </div>
              {withNewBadge && hideName && (
                <GradientBadge>
                  <Typography variant="prompt" gradient>
                    New!
                  </Typography>
                </GradientBadge>
              )}
            </div>
          )}
          {points && (
            <PointsBadge
              action={points.action}
              asset={points.asset}
              multiplier={points?.multiplier}
              partnerPoints={points?.partnerPoints}
              extraIncentives={points?.extraIncentives}
            />
          )}
        </div>
      </div>
      {isFrozen && <FrozenPill />}
      {isPaused && <PausedPill />}
      {isCombinedBalance && <Info>WHYPE and HYPE balances are combined.</Info>}
    </div>
  )
}
